import './ledger.css';
import { useEffect, useState } from 'react';
import {
  fetchFamilies, fetchDeadlines, createFamily, updateFamily, createFiling,
  createDeadline, updateDeadline, deleteDeadline, daysTo,
  FAMILY_STATUS, STRATEGIC_VALUE, FTO_STATUS, DEADLINE_TYPES, DEADLINE_STATUS, RESPONSIBLE,
  type Family, type Deadline,
} from '../lib/docket';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';

/* =====================================================================================
   Docket — patent families, filings and the deadlines that end them.

   The content here is real: six families, their GB provisionals, and the standing watch on
   Curt Lind and Heritage Diagnostics. It used to be hardcoded in this file; it now lives in
   the database (db/seed/docket_seed.sql) so it can be added to and edited.

   Missing a renewal or the Paris twelve-month deadline is unrecoverable, and nobody sends a
   reminder: the attorney bills for what you instruct and the office simply lets it lapse.
   So every action carries who is responsible, because "someone was handling it" is exactly
   how a family is lost.
   ===================================================================================== */

export function DocketView() {
  const [families, setFamilies] = useState<Family[]>([]);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [tab, setTab] = useState<'families' | 'actions'>('actions');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    fetchFamilies().then(setFamilies).catch((e) => setErr(String(e.message ?? e)));
    fetchDeadlines().then(setDeadlines).catch(onFail('Deadlines', () => setDeadlines([])));
  }, [nonce]);

  const open = deadlines.filter((d) => d.status === 'open' || d.status === 'instructed');

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Docket</h1>
        <p className="page-sub">
          Patent families, national filings and dated obligations. The Paris deadline is
          computed from the priority date by the database and never typed by hand: a mistyped
          date on the one deadline that cannot be extended is the most expensive typo here.
        </p>
      </div>

      {err && <p className="block-error" role="alert">{err}</p>}

      <div className="q-tabs">
        <button className={tab === 'actions' ? 'on' : ''} onClick={() => setTab('actions')}>
          Actions ({open.length} open)
        </button>
        <button className={tab === 'families' ? 'on' : ''} onClick={() => setTab('families')}>
          Families ({families.length})
        </button>
        {tab === 'families' && (
          <button className="btn-compose q-new" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : 'New family'}
          </button>
        )}
      </div>

      {tab === 'actions' && (
        <ActionsTab deadlines={deadlines} families={families} onChanged={reload} />
      )}

      {tab === 'families' && (
        <>
          {adding && <NewFamily onDone={() => { setAdding(false); reload(); }} />}
          {families.length === 0 && !adding && (
            <p className="block-empty">
              No families recorded. Run db/seed/docket_seed.sql to load the six real ones.
            </p>
          )}
          {families.map((f) => <FamilyCard key={f.id} family={f} onChanged={reload} />)}
        </>
      )}
    </div>
  );
}

function ActionsTab({ deadlines, families, onChanged }: {
  deadlines: Deadline[]; families: Family[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const live = deadlines.filter((d) => d.status === 'open' || d.status === 'instructed');
  const done = deadlines.filter((d) => !live.includes(d));

  async function setStatus(d: Deadline, status: string) {
    setErr(null);
    try { await updateDeadline(d.id, { status }); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <>
      <div className="g-call-actions">
        <button className="btn-compose" disabled={!families.length}
                onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : 'Set an action'}
        </button>
        {!families.length && <span className="g-hint">Add a family first.</span>}
      </div>

      {adding && <NewDeadline families={families} onDone={() => { setAdding(false); onChanged(); }} />}
      {err && <p className="block-error" role="alert">{err}</p>}
      {!live.length && !adding && <p className="block-empty">Nothing outstanding.</p>}

      {live.map((d) => {
        const n = daysTo(d.due_date);
        const open = expanded === d.id;
        return (
          <article key={d.id} className={`card entry ${d.critical || (n ?? 99) < 60 ? 'sev-critical' : 'sev-normal'}`}>
            <div className="entry-when">
              <div className="entry-days">{n ?? '—'}</div>
              <div className="entry-days-l">days</div>
            </div>
            <div className="entry-main">
              <header className="entry-head">
                <h3>
                  <button className="dk-title" onClick={() => setExpanded(open ? null : d.id)}>
                    {d.description ?? (d.deadline_type ?? 'deadline').replace(/_/g, ' ')}
                  </button>
                </h3>
                <div className="q-tags">
                  {d.critical && <span className="pill risk">critical</span>}
                  {/* Responsibility is shown on the card, not buried in a detail view: an
                      unassigned deadline is the failure this module exists to prevent. */}
                  {/* A deadline with nobody responsible is shown as loudly as a critical one.
                      It used to throw here (null.replace) and take the whole page down, which
                      hid the very record that needed attention (20 Oct 2026). */}
                  <span className={`pill ${d.responsible === 'us' ? 'warn' : d.responsible ? 'mute' : 'risk'}`}>
                    {d.responsible === 'us' ? 'ours' : d.responsible ? d.responsible.replace('_', ' ') : 'nobody responsible'}
                  </span>
                </div>
              </header>
              <footer className="entry-foot">
                <span className="pill mute">{(d.deadline_type ?? 'deadline').replace(/_/g, ' ')}</span>
                {d.family_ref && <span className="mono">{d.family_ref}</span>}
                {d.jurisdiction && <span>{d.jurisdiction}</span>}
                <span className="entry-date">
                  {new Date(d.due_date).toLocaleDateString('en-GB',
                    { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <select value={d.status} onChange={(e) => setStatus(d, e.target.value)}>
                  {DEADLINE_STATUS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
                <button className="link-btn" onClick={() => setExpanded(open ? null : d.id)}>
                  {open ? 'Close' : 'Edit'}
                </button>
              </footer>

              {open && (
                <div className="dk-edit">
                  <div className="composer-row">
                    <label className="composer-ack">Due
                      <input type="date" defaultValue={d.due_date}
                             onBlur={(e) => e.target.value !== d.due_date
                               && updateDeadline(d.id, { due_date: e.target.value }).then(onChanged)} />
                    </label>
                    <label className="composer-ack">Extendable to
                      <input type="date" defaultValue={d.extendable_to ?? ''}
                             onBlur={(e) => e.target.value !== (d.extendable_to ?? '')
                               && updateDeadline(d.id, { extendable_to: e.target.value || null }).then(onChanged)} />
                    </label>
                    <label className="composer-ack">Responsible
                      <select defaultValue={d.responsible ?? ''}
                              onChange={(e) => updateDeadline(d.id, { responsible: e.target.value }).then(onChanged)}>
                        {RESPONSIBLE.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                      </select>
                    </label>
                    <label className="composer-ack">
                      <input type="checkbox" defaultChecked={d.critical}
                             onChange={(e) => updateDeadline(d.id, { critical: e.target.checked }).then(onChanged)} />
                      Unrecoverable if missed
                    </label>
                  </div>
                  <input className="dk-desc" defaultValue={d.description ?? ''}
                         placeholder="What has to happen"
                         onBlur={(e) => e.target.value !== (d.description ?? '')
                           && updateDeadline(d.id, { description: e.target.value }).then(onChanged)} />
                  <div className="dk-edit-foot">
                    {/* Deleting is for something entered in error. A completed or missed
                        action is prosecution history and the library refuses to remove it. */}
                    <button className="att-remove"
                            onClick={() => deleteDeadline(d.id, d.status)
                              .then(onChanged)
                              .catch((e) => setErr(String(e.message ?? e)))}>
                      Remove this action
                    </button>
                    <span className="muted">Changes save when you leave each field.</span>
                  </div>
                </div>
              )}
            </div>
          </article>
        );
      })}

      {done.length > 0 && (
        <details className="dk-done">
          <summary>{done.length} closed</summary>
          {done.map((d) => (
            <div key={d.id} className="dk-done-row">
              <span className="mono">{d.family_ref}</span> {d.description ?? d.deadline_type}
              <span className="pill mute">{d.status}</span>
            </div>
          ))}
        </details>
      )}
    </>
  );
}

function NewDeadline({ families, onDone }: { families: Family[]; onDone: () => void }) {
  const [f, setF] = useState({
    familyId: '', applicationId: '', deadlineType: 'renewal_annuity',
    description: '', dueDate: '', responsible: 'attorney' as const,
    critical: false, feeAmount: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const family = families.find((x) => x.id === f.familyId);

  async function save() {
    setErr(null);
    try {
      await createDeadline({
        familyId: f.familyId || null,
        applicationId: f.applicationId || null,
        deadlineType: f.deadlineType, description: f.description,
        dueDate: f.dueDate, responsible: f.responsible,
        critical: f.critical, feeAmount: f.feeAmount ? Number(f.feeAmount) : undefined,
      });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <div className="composer card q-form">
      <div className="composer-row">
        <select value={f.familyId} onChange={(e) => setF({ ...f, familyId: e.target.value, applicationId: '' })}>
          <option value="">Family…</option>
          {families.map((x) => <option key={x.id} value={x.id}>{x.family_ref}: {x.title}</option>)}
        </select>
        {/* Optional: an action can hang off the family, or off one national filing. A
            renewal is per filing; a priority-year decision is per family. */}
        <select value={f.applicationId} disabled={!family?.applications?.length}
                onChange={(e) => setF({ ...f, applicationId: e.target.value })}>
          <option value="">Whole family</option>
          {(family?.applications ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.jurisdiction}{a.application_number ? ` ${a.application_number}` : ''}
            </option>
          ))}
        </select>
        <select value={f.deadlineType} onChange={(e) => setF({ ...f, deadlineType: e.target.value })}>
          {DEADLINE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
      <div className="composer-row">
        <label className="composer-ack">Due
          <input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
        </label>
        <select value={f.responsible} onChange={(e) => setF({ ...f, responsible: e.target.value as any })}>
          {RESPONSIBLE.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
        </select>
        <input type="number" placeholder="Fee" value={f.feeAmount}
               onChange={(e) => setF({ ...f, feeAmount: e.target.value })} style={{ maxWidth: '8rem' }} />
        <label className="composer-ack">
          <input type="checkbox" checked={f.critical}
                 onChange={(e) => setF({ ...f, critical: e.target.checked })} />
          Unrecoverable if missed
        </label>
      </div>
      <input placeholder="What has to happen" value={f.description}
             onChange={(e) => setF({ ...f, description: e.target.value })} />
      {err && <p className="composer-err" role="alert">{err}</p>}
      <div className="composer-actions">
        <button className="btn-save" disabled={!f.familyId || !f.dueDate} onClick={save}>
          Set action
        </button>
      </div>
    </div>
  );
}

function NewFamily({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({
    familyRef: '', title: '', pillar: '', priorityDate: '', priorityApplication: '',
    // The proprietor of a patent family is the tenant, not a hardcoded company.
    proprietor: TENANT.brand.orgName, attorneyFirm: '', strategicValue: 'important',
  });
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="composer card q-form">
      <div className="composer-row">
        <input placeholder="Reference, e.g. SC-NEW-1" value={f.familyRef} style={{ maxWidth: '11rem' }}
               onChange={(e) => setF({ ...f, familyRef: e.target.value })} />
        <input placeholder="Title" className="grow" value={f.title}
               onChange={(e) => setF({ ...f, title: e.target.value })} />
      </div>
      <div className="composer-row">
        <input placeholder="Pillar" value={f.pillar} onChange={(e) => setF({ ...f, pillar: e.target.value })} />
        <input placeholder="Proprietor" value={f.proprietor}
               onChange={(e) => setF({ ...f, proprietor: e.target.value })} />
        <label className="composer-ack">Priority
          <input type="date" value={f.priorityDate}
                 onChange={(e) => setF({ ...f, priorityDate: e.target.value })} />
        </label>
        <select value={f.strategicValue} onChange={(e) => setF({ ...f, strategicValue: e.target.value })}>
          {STRATEGIC_VALUE.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <p className="correction-note">
        The Paris deadline is computed from the priority date. It is not a field here because
        it must never be entered by hand.
      </p>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <div className="composer-actions">
        <button className="btn-save" disabled={!f.familyRef.trim() || !f.title.trim()}
                onClick={() => createFamily(f).then(onDone).catch((e) => setErr(String(e.message ?? e)))}>
          Create family
        </button>
      </div>
    </div>
  );
}

function FamilyCard({ family, onChanged }: { family: Family; onChanged: () => void }) {
  const [addFiling, setAddFiling] = useState(false);
  const [n, setN] = useState({ jurisdiction: '', applicationType: 'provisional', applicationNumber: '', filedOn: '' });
  const [err, setErr] = useState<string | null>(null);
  const paris = daysTo(family.paris_deadline);

  return (
    <article className="card q-doc">
      <header className="q-doc-head">
        <div>
          <span className="mono q-num">{family.family_ref}</span>
          <h3>{family.title}</h3>
          <div className="g-app-sub">
            {family.pillar}{family.proprietor ? ` · ${family.proprietor}` : ''}
          </div>
        </div>
        <div className="q-tags">
          <span className={`pill ${family.strategic_value === 'core' ? 'accent' : 'mute'}`}>
            {family.strategic_value}
          </span>
          <span className={`pill ${family.fto_status === 'blocked' ? 'risk'
            : family.fto_status === 'concerns' ? 'warn'
            : family.fto_status === 'not_assessed' ? 'mute' : 'ok'}`}>
            FTO {family.fto_status?.replace('_', ' ')}
          </span>
          <select value={family.fto_status ?? 'not_assessed'} title="Freedom to operate"
                  onChange={(e) => updateFamily(family.id, { fto_status: e.target.value }).then(onChanged)}>
            {FTO_STATUS.map((v) => <option key={v} value={v}>FTO {v.replace('_', ' ')}</option>)}
          </select>
          <select value={family.status}
                  onChange={(e) => updateFamily(family.id, { status: e.target.value }).then(onChanged)}>
            {FAMILY_STATUS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
      </header>

      <dl className="q-meta">
        <dt>Priority</dt><dd>{family.priority_date ?? 'not filed'}</dd>
        <dt>Paris deadline</dt>
        <dd>
          {family.paris_deadline
            ? <>{new Date(family.paris_deadline).toLocaleDateString('en-GB')}{' '}
                <span className={`pill ${(paris ?? 999) < 90 ? 'risk' : 'mute'}`}>{paris} days</span></>
            : '—'}
        </dd>
        {family.attorney_firm && (<><dt>Attorney</dt><dd>{family.attorney_firm}</dd></>)}
      </dl>

      <div className="dk-filings">
        {(family.applications ?? []).map((a) => (
          <span key={a.id} className="dk-filing">
            <strong>{a.jurisdiction}</strong> {a.application_type}
            {a.application_number && <span className="mono"> {a.application_number}</span>}
            <span className="pill mute">{a.status}</span>
          </span>
        ))}
        <button className="link-btn" onClick={() => setAddFiling(!addFiling)}>
          {addFiling ? 'Cancel' : 'Add filing'}
        </button>
      </div>

      {addFiling && (
        <div className="q-action">
          <input placeholder="GB, EP, US…" value={n.jurisdiction} style={{ maxWidth: '7rem' }}
                 onChange={(e) => setN({ ...n, jurisdiction: e.target.value })} />
          <select value={n.applicationType} onChange={(e) => setN({ ...n, applicationType: e.target.value })}>
            <option value="provisional">provisional</option>
            <option value="pct">PCT</option>
            <option value="national">national</option>
            <option value="divisional">divisional</option>
            <option value="continuation">continuation</option>
          </select>
          <input placeholder="Application number" value={n.applicationNumber}
                 onChange={(e) => setN({ ...n, applicationNumber: e.target.value })} />
          <button className="btn-save" disabled={!n.jurisdiction.trim()}
                  onClick={() => createFiling({ familyId: family.id, ...n })
                    .then(() => { setAddFiling(false); setN({ jurisdiction: '', applicationType: 'provisional', applicationNumber: '', filedOn: '' }); onChanged(); })
                    .catch((e) => setErr(String(e.message ?? e)))}>Add</button>
        </div>
      )}
      {err && <p className="composer-err" role="alert">{err}</p>}
    </article>
  );
}
